/**
 * SystemWarning Store
 *
 * Manages system-level warnings, warning components, and system messages.
 */

import { type SetStoreState, createStore } from '@seeqdev/utils';

export interface SystemWarningState {
  warningComponents: string[];
  systemMessage: string;
  showSystemWarning: boolean;
  suppressUserLimitWarning: boolean;
}

const initialState: SystemWarningState = {
  warningComponents: [],
  systemMessage: '',
  showSystemWarning: true,
  suppressUserLimitWarning: false,
};

const systemWarningActionsFactory = (set: SetStoreState<SystemWarningState>) => ({
  setWarningComponents: (components: string[]) =>
    set((draft: SystemWarningState) => {
      draft.warningComponents = components.slice();
    }),

  setSystemMessage: (message: string) =>
    set((draft: SystemWarningState) => {
      draft.systemMessage = message;
    }),

  disableSystemWarning: () =>
    set((draft: SystemWarningState) => {
      draft.showSystemWarning = false;
    }),

  disableUserLimitWarning: () =>
    set((draft: SystemWarningState) => {
      draft.suppressUserLimitWarning = true;
    }),

  reset: () => set(() => initialState),
});

const [SystemWarningStoreProvider, useSystemWarningStoreInstance, useSystemWarningSelector] = createStore(
  'SystemWarning',
  initialState,
  systemWarningActionsFactory,
);

export { SystemWarningStoreProvider };

/**
 * Hook that only re-renders when warningComponents changes
 */
export const useWarningComponents = () => useSystemWarningSelector((state) => state.warningComponents);

/**
 * Hook that only re-renders when systemMessage changes
 */
export const useSystemMessage = () => useSystemWarningSelector((state) => state.systemMessage);

/**
 * Hook that only re-renders when showSystemWarning changes
 */
export const useShowSystemWarning = () => useSystemWarningSelector((state) => state.showSystemWarning);

/**
 * Hook that only re-renders when suppressUserLimitWarning changes
 */
export const useSuppressUserLimitWarning = () => useSystemWarningSelector((state) => state.suppressUserLimitWarning);

/**
 * Hook that provides action functions
 */
export const useSystemWarningActions = () => useSystemWarningStoreInstance().actions;
